import { InfoCircle, MapPin, Page, WarningTriangle } from 'iconoir-react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';

import { BUTTON_TYPES, COUNTRY_CODES } from '@savgroup-front-common/constants';
import {
  Autocomplete,
  Input,
  PlacesAutocomplete,
} from '@savgroup-front-common/core/atoms/Form';
import { FieldMessage } from '@savgroup-front-common/core/atoms/Form/common';
import { FIELD_STATUS } from '@savgroup-front-common/core/atoms/Form/common/helpers/getFinalFieldState.types';
import { PhoneNumberInput } from '@savgroup-front-common/core/atoms/Form/PhoneNumberInput/PhoneNumberInput';
import { AssistantStatus } from '@savgroup-front-common/core/components/AssistantBricks/AssistantStatus/AssistantStatus';
import { DesignationHalo } from '@savgroup-front-common/core/components/AssistantBricks/DesignationHalo/DesignationHalo';
import { HALO_STATUS } from '@savgroup-front-common/core/components/AssistantBricks/DesignationHalo/DesignationHalo.types';
import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '@savgroup-front-common/core/formatters';
import { getAddress } from '@savgroup-front-common/core/helpers';
import { InputHookForm } from '@savgroup-front-common/core/molecules/Form';
import { cn } from '@savgroup-front-common/primitive/lib/utils';
import {
  Alert,
  AlertDescription,
} from '@savgroup-front-common/primitive/ui/alert';
import { Badge } from '@savgroup-front-common/primitive/ui/badge';
import { PrimitiveButton } from '@savgroup-front-common/primitive/ui/button';
import { Spinner } from '@savgroup-front-common/primitive/ui/spinner';

import { useCompanyProfileSuggestion } from '../../OnboardingActivities/hooks/useCompanyProfileSuggestion';
import { hasActivitiesProposal } from '../../OnboardingActivities/OnboardingActivities.helpers';

import AgreementCheckbox from './AgreementCheckbox/AgreementCheckbox';
import { CompanySearchField } from './CompanySearchField/CompanySearchField';
import useOnboardingFormContext from './hooks/useOnboardingFormContext';
import { useSuggestedWebsite } from './hooks/useSuggestedWebsite';
import messages from './messages';
import {
  formatCNameInput,
  formatCompanyDetails,
} from './OnboardingForm.formatters';
import {
  getSourceMessage,
  prepareEmailForCompanyProfile,
} from './OnboardingForm.helpers';

const SECTION_TITLE_CLASS_NAME = cn(
  'mt-8 mb-4 flex flex-wrap items-center gap-2 border-t sborder-base-default pt-6',
  'text-xs font-bold tracking-wider uppercase stext-muted-default',
  'first:mt-0 first:border-t-0 first:pt-0',
);

const OnboardingForm = ({
  handleUserEmail,
}: {
  handleUserEmail: (userEmail: string) => void;
}) => {
  const {
    handleFormSubmit,
    handleSelectAddress,
    handleSelectCompany,
    companyWithRegisterAddress,
    pickedCompany,
    clearPickedCompany,
    formContext,
    isLoading,
    autoCName,
    countryOptions,
  } = useOnboardingFormContext({ handleUserEmail });
  const {
    suggestedCName,
    domainSuffix,
    isResolvingCName,
    hasNoSuggestedCName,
    isCNameEdited,
    startEditingCName,
    restoreSuggestedCName,
  } = autoCName;
  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    watch,
    setValue,
  } = formContext;

  const watchedCountryCode = watch('countryCode');
  const watchedPhoneCountryCode = watch('phoneCountryCode');
  const selectedCountryCode = watchedCountryCode?.value?.toUpperCase();
  const phoneInputCountryCode =
    selectedCountryCode ||
    watchedPhoneCountryCode?.toUpperCase() ||
    COUNTRY_CODES.FR;

  const [isManualCompany, setIsManualCompany] = useState(false);
  const [profileEmail, setProfileEmail] = useState<string>();
  const {
    proposedProfile,
    isLoading: isProfileLoading,
    hasSuggestionFailed,
    hasPickedCompany,
    website: suggestedWebsite,
    isWebsiteVerified,
  } = useCompanyProfileSuggestion({
    pickedCompany,
    email: profileEmail,
  });
  const {
    shouldShowWebsitePreview,
    shouldShowWebsiteField,
    startEditingWebsite,
    suggestedWebsite: previewWebsite,
  } = useSuggestedWebsite({
    formContext,
    suggestedWebsite,
    isWebsiteVerified,
    pickedCompanyKey: pickedCompany
      ? `${pickedCompany.source}:${pickedCompany.registrationNumber}`
      : undefined,
  });
  const hasProfileProposal =
    hasPickedCompany &&
    !hasSuggestionFailed &&
    hasActivitiesProposal(proposedProfile);
  const nextStepPrefilled = hasProfileProposal
    ? safeFormattedIntlString(messages.nextStepPrefilled)
    : undefined;

  const companyName = watch('companyName');
  const companyLabel = companyName || (
    <SafeFormattedMessageWithoutSpread message={messages.yourCompany} />
  );
  const companyHaloStatus = isProfileLoading
    ? HALO_STATUS.WORKING
    : HALO_STATUS.IDLE;
  const companySourceMessage =
    companyWithRegisterAddress &&
    getSourceMessage(companyWithRegisterAddress.source);

  const watchedCName = watch('cName');
  const previewCName = isCNameEdited
    ? watchedCName?.trim()
    : suggestedCName?.cName;
  const shouldShowCustomerSpacePreview = !!previewCName && !!domainSuffix;
  const canEditCName = !isCNameEdited && hasNoSuggestedCName;

  return (
    <form className="flex flex-col" onSubmit={handleSubmit(handleFormSubmit)}>
      <h2 className={SECTION_TITLE_CLASS_NAME}>
        <SafeFormattedMessageWithoutSpread message={messages.companySection} />
      </h2>
      <div className="flex flex-col gap-5">
        <Controller
          control={control}
          name="companyName"
          render={({ field }) => (
            <DesignationHalo
              status={companyHaloStatus}
              dataTestId="OnboardingForm-companyHalo"
            >
              <CompanySearchField
                value={field.value || ''}
                countryCode={watchedCountryCode?.value}
                errorMessage={errors.companyName?.message}
                onChange={(companyName) => {
                  field.onChange(companyName);
                  clearPickedCompany();
                }}
                onCompanySelected={(suggestion) => {
                  setIsManualCompany(false);
                  handleSelectCompany(suggestion);
                }}
                onManualEntry={() => {
                  setIsManualCompany(true);
                  clearPickedCompany();
                }}
              />
            </DesignationHalo>
          )}
        />
        {companyWithRegisterAddress && (
          <Alert data-testid="OnboardingForm-companyFromRegister">
            <InfoCircle aria-hidden className="size-4" />
            <AlertDescription>
              <SafeFormattedMessageWithoutSpread
                message={messages.companyFromRegister}
                values={{
                  company: (
                    <span className="font-semibold">
                      {companyWithRegisterAddress.name}
                    </span>
                  ),
                  details: formatCompanyDetails(companyWithRegisterAddress),
                  nextStepPrefilled,
                }}
              />
              {companySourceMessage && (
                <Badge variant="subtle">
                  <Page aria-hidden />
                  <SafeFormattedMessageWithoutSpread
                    message={companySourceMessage}
                  />
                </Badge>
              )}
            </AlertDescription>
          </Alert>
        )}
        {isManualCompany && (
          <Alert variant="warning" data-testid="OnboardingForm-manualCompany">
            <WarningTriangle aria-hidden className="size-4" />
            <AlertDescription>
              <SafeFormattedMessageWithoutSpread
                message={messages.manualCompany}
                values={{
                  company: (
                    <span className="font-semibold">{companyLabel}</span>
                  ),
                }}
              />
            </AlertDescription>
          </Alert>
        )}
        {isCNameEdited && (
          <Controller
            control={control}
            name="cName"
            render={({ field }) => (
              <Input
                name={field.name}
                value={field.value || ''}
                label={messages.customerSpaceLabel}
                suffix={domainSuffix}
                errors={errors}
                disableBrowserAutocomplete
                isRequired
                onChange={(event) =>
                  field.onChange(formatCNameInput(event.target.value))
                }
                dataTestId="OnboardingForm-cName"
              />
            )}
          />
        )}
        {(shouldShowCustomerSpacePreview || isCNameEdited || canEditCName) && (
          <p
            className="text-xs font-medium stext-muted-default"
            data-testid="OnboardingForm-customerSpacePreview"
          >
            {shouldShowCustomerSpacePreview && (
              <SafeFormattedMessageWithoutSpread
                message={messages.customerSpacePreview}
                values={{
                  domainName: (
                    <span className="inline-flex">
                      <span className="font-bold stext-base-default">
                        {previewCName}
                      </span>
                      {domainSuffix}
                    </span>
                  ),
                }}
              />
            )}
            {!isCNameEdited && (
              <PrimitiveButton
                type={BUTTON_TYPES.BUTTON}
                variant="link"
                size="xs"
                onClick={startEditingCName}
                data-testid="OnboardingForm-cName-edit"
              >
                <SafeFormattedMessageWithoutSpread
                  message={messages.customerSpaceEdit}
                />
              </PrimitiveButton>
            )}
            {isCNameEdited && suggestedCName && (
              <PrimitiveButton
                type={BUTTON_TYPES.BUTTON}
                variant="link"
                size="xs"
                onClick={restoreSuggestedCName}
                data-testid="OnboardingForm-cName-useSuggested"
              >
                <SafeFormattedMessageWithoutSpread
                  message={messages.customerSpaceUseSuggested}
                />
              </PrimitiveButton>
            )}
          </p>
        )}
        {!isProfileLoading && shouldShowWebsitePreview && previewWebsite && (
          <p
            className="text-xs font-medium stext-muted-default"
            data-testid="OnboardingForm-websitePreview"
          >
            <SafeFormattedMessageWithoutSpread
              message={messages.websitePreview}
              values={{
                website: (
                  <span className="font-bold stext-base-default">
                    {previewWebsite}
                  </span>
                ),
              }}
            />
            {!isWebsiteVerified && (
              <>
                {' · '}
                <SafeFormattedMessageWithoutSpread
                  message={messages.websiteToCheck}
                />
              </>
            )}
            {' · '}
            <PrimitiveButton
              type={BUTTON_TYPES.BUTTON}
              variant="link"
              size="xs"
              onClick={startEditingWebsite}
              data-testid="OnboardingForm-website-edit"
            >
              <SafeFormattedMessageWithoutSpread
                message={messages.customerSpaceEdit}
              />
            </PrimitiveButton>
          </p>
        )}

        {!isProfileLoading &&
          ((shouldShowWebsiteField && (hasPickedCompany || isManualCompany)) ||
            !!errors.website) && (
            <Controller
              control={control}
              name="website"
              render={({ field }) => (
                <Input
                  name={field.name}
                  value={field.value || ''}
                  label={messages.website}
                  errors={errors}
                  disableBrowserAutocomplete
                  onChange={(event) => field.onChange(event.target.value)}
                  dataTestId="OnboardingForm-website"
                />
              )}
            />
          )}
      </div>
      <h2 className={SECTION_TITLE_CLASS_NAME}>
        <SafeFormattedMessageWithoutSpread message={messages.youSection} />
      </h2>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-4">
        <InputHookForm
          name="lastName"
          errors={errors}
          label={messages.lastName}
          register={register}
          isRequired
          dataTestId="OnboardingForm-lastName"
        />
        <InputHookForm
          name="firstName"
          errors={errors}
          label={messages.firstName}
          register={register}
          isRequired
          dataTestId="OnboardingForm-firstName"
        />
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <Input
              name={field.name}
              value={field.value || ''}
              label={messages.email}
              errors={errors}
              disableBrowserAutocomplete
              isRequired
              onChange={(event) => field.onChange(event.target.value)}
              onBlur={(event) => {
                field.onBlur();
                setProfileEmail(
                  prepareEmailForCompanyProfile(event.target.value),
                );
              }}
              dataTestId="OnboardingForm-email"
            />
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field }) => {
            return (
              <PhoneNumberInput
                key={`OnboardingForm-phone-${phoneInputCountryCode}`}
                name={field.name}
                value={field.value}
                label={messages.phone}
                dataTestId="OnboardingForm-phone"
                disableBrowserAutocomplete
                errors={errors}
                defaultCountry={phoneInputCountryCode}
                onBlur={field.onBlur}
                onChange={(
                  newValue: string,
                  meta?: {
                    country: {
                      iso2: string;
                    };
                  },
                ) => {
                  setValue('phone', newValue, {
                    shouldDirty: true,
                    shouldTouch: false,
                    shouldValidate: false,
                  });

                  if (!meta) {
                    return;
                  }

                  const metaCountryCode = meta.country.iso2.toUpperCase();

                  setValue(
                    'phoneCountryCode',
                    metaCountryCode as COUNTRY_CODES,
                    {
                      shouldTouch: false,
                      shouldValidate: false,
                    },
                  );
                }}
                isRequired
              />
            );
          }}
        />
      </div>
      <h2 className={SECTION_TITLE_CLASS_NAME}>
        <SafeFormattedMessageWithoutSpread message={messages.addressSection} />
        {companyWithRegisterAddress && (
          <Badge
            variant="subtle"
            className="tracking-normal normal-case"
            data-testid="OnboardingForm-addressFromRegister"
          >
            <MapPin aria-hidden />
            <SafeFormattedMessageWithoutSpread
              message={messages.addressFromRegister}
            />
          </Badge>
        )}
      </h2>
      <div className="flex flex-col gap-5">
        <Controller
          control={control}
          name="address"
          render={({ field }) => {
            return (
              <PlacesAutocomplete
                name={field.name}
                value={field.value}
                label={messages.address}
                errors={errors}
                isRequired
                onSelect={(values) => {
                  handleSelectAddress(values);
                  field.onChange(
                    getAddress(values.street, values.streetNumber),
                  );
                }}
                onChange={field.onChange}
                countryCode={watchedCountryCode?.value}
                dataTestId="OnboardingForm-address"
              />
            );
          }}
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 sm:gap-x-4">
          <Controller
            control={control}
            name="postalCode"
            render={({ field }) => {
              return (
                <Input
                  name={field.name}
                  value={field.value}
                  label={messages.postalCode}
                  disableBrowserAutocomplete
                  onChange={field.onChange}
                  errors={errors}
                  isRequired
                  dataTestId="OnboardingForm-postalCode"
                />
              );
            }}
          />
          <Controller
            control={control}
            name="city"
            render={({ field }) => {
              return (
                <Input
                  name={field.name}
                  value={field.value}
                  label={messages.city}
                  disableBrowserAutocomplete
                  onChange={field.onChange}
                  errors={errors}
                  isRequired
                  dataTestId="OnboardingForm-city"
                />
              );
            }}
          />
          <Controller
            control={control}
            name="countryCode"
            render={({ field }) => {
              return (
                <Autocomplete
                  name={field.name}
                  value={field.value}
                  label={messages.country}
                  onChange={(option, meta) => {
                    field.onChange(option, meta);

                    const countryCode = option?.value?.toUpperCase();

                    if (!countryCode) {
                      return;
                    }

                    setValue('phoneCountryCode', countryCode as COUNTRY_CODES, {
                      shouldTouch: false,
                      shouldValidate: false,
                    });

                    setValue('phone', '');
                  }}
                  options={countryOptions}
                  errors={errors}
                  isRequired
                  dataTestId="OnboardingForm-country"
                />
              );
            }}
          />
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-5 border-t sborder-base-default pt-6 md:flex-row md:items-center md:justify-between">
        <div>
          <Controller
            control={control}
            name="cgv"
            render={({ field }) => {
              return (
                <AgreementCheckbox
                  isChecked={field.value}
                  onChange={field.onChange}
                  isError={!!errors.cgv?.message}
                  dataTestId="OnboardingForm-cgv"
                />
              );
            }}
          />
          <FieldMessage
            message={errors.cgv?.message}
            status={FIELD_STATUS.ERROR}
          />
        </div>
        <PrimitiveButton
          type="submit"
          size="lg"
          className="w-full shrink-0 px-5 md:w-auto"
          disabled={isLoading || isResolvingCName}
          data-testid="OnboardingForm-submit"
        >
          {(isLoading || isResolvingCName) && <Spinner />}
          <SafeFormattedMessageWithoutSpread message={messages.trialStart} />
        </PrimitiveButton>
      </div>
      {isProfileLoading && (
        <AssistantStatus dataTestId="OnboardingForm-analysingCompany">
          <SafeFormattedMessageWithoutSpread
            message={messages.analysingCompany}
            values={{ company: companyLabel }}
          />
        </AssistantStatus>
      )}
    </form>
  );
};

OnboardingForm.displayName = 'OnboardingForm';

export default OnboardingForm;
